import { Button } from "@mui/material";
import BillToBox from "components/Common/BillToShipToBox/BillToBox/BillToBox";
import { FlowButtons } from "components/NewOrders/NewOrders.styles";
import ShippingOrderDetails from "components/NewOrders/ShippingOrderDetails";
import { NewOrderShippingDetailsLabel } from "components/NewOrders/constants";
import ReplacementsHeader from "components/Replacements/ReplacementsHeader";
import ShipToForm from "components/ShipToForm/ShipToForm";
import { BackText, ContinueText, SelectBillToText, UnavailableDataPlaceholderText } from "constants/text";
import { OrderType } from "types/declarations/enums.ts";
import { useGetAccountsByIdQuery } from "features/api/accountApi";
import { useGetByIdQuery } from "features/api/orderApi";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { AddressFormFields } from "./forms/AddressForm/schema";
import { models } from "types/api/viewModels";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import { ReplacementsShippingDetailsPageContent } from "./ReplacementsShippingDetailsPage.styles";
import { submittingReplacementOrderActions } from "features/reducers/replacementOrder/submittingReplacementOrder";
import { formatPhoneNumber } from "components/Replacements/ReplacementsUtil";
import AutoComplete from "components/Common/Autocomplete/Autocomplete";
import { FormSchema } from "components/NewOrders/NewOrderShippingDetailsForm/schema";

const ReplacementsShippingDetailsPage = () => {
	const params = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { getValues, handleSubmit, control, setValue, watch } = useFormContext<FormSchema>();

	const { data: orderDetails } = useGetByIdQuery(params?.orderId ?? "", { skip: !params?.orderId });
	const { data: accounts, isLoading: isAccountsLoading } = useGetAccountsByIdQuery(params?.orderId ?? "");

	const replacementOrder = useSelector((state: RootState) => state.submittingReplacementOrder.order);
	const replacementOrderBillTo = replacementOrder?.billingDetails;

	const addedAddresses = useSelector((state: RootState) => state.submittingReplacementOrder.newAddresses);

	const allBillTos = useMemo(() => accounts?.flatMap((billTo) => billTo?.billTos ?? []) ?? [], [accounts]);
	const activeBillTos = useMemo(() => allBillTos.filter((billTo) => billTo.isActive), [allBillTos]);
	const orderBillTo = useMemo(
		() => allBillTos?.find((billTo) => billTo.billToId === orderDetails?.billToId),
		[allBillTos, orderDetails?.billToId]
	);

	const formBillTo = watch("billTo");
	const [combinedAddresses, setCombinedAddresses] = useState<models["ShipToViewModel"][]>([]);

	useEffect(() => {
		const selectedBillTo = formBillTo ?? orderBillTo;
		const addresses = [
			...(
				accounts?.map(
					(account) =>
						account.billTos?.find((billTo) => billTo?.billToId === selectedBillTo?.billToId)?.shipTos
				) ?? []
			)
				.flat()
				.filter((address) => typeof address !== "undefined" && address !== null),
			...addedAddresses
		];

		setCombinedAddresses(addresses);
	}, [accounts, addedAddresses, orderBillTo, formBillTo, formBillTo?.billToId]);

	useEffect(() => {
		const selectedAddress = combinedAddresses.find((address) => address.isSelected);
		setValue(
			"address",
			(formBillTo?.isActive ?? orderBillTo?.isActive)
				? (replacementOrder?.shippingDetails ?? selectedAddress)
				: undefined
		);
		if (!getValues("billTo") && replacementOrder?.billingDetails?.isActive) {
			setValue("billTo", replacementOrder?.billingDetails);
		}
		setValue("poNumber", replacementOrder?.poNumber ?? orderDetails?.poNumber);
		setValue("jobName", replacementOrder?.jobName ?? orderDetails?.jobName);
		setValue("email", replacementOrder?.shippingDetails?.address?.email ?? selectedAddress?.address?.email ?? null);
		setValue(
			"phoneNumber",
			formatPhoneNumber(replacementOrder?.shippingDetails?.address?.phoneNumber) ??
				formatPhoneNumber(selectedAddress?.address?.phoneNumber) ??
				null
		);
		setValue("deliveryInstructions", replacementOrder?.labelComments);
		setValue("isCustomerPickup", replacementOrder?.isCustomerPickup);
		setValue("designer", orderDetails?.designerNumber);
	}, [
		formBillTo?.isActive,
		setValue,
		replacementOrder,
		orderDetails,
		combinedAddresses,
		orderBillTo?.isActive,
		getValues
	]);

	useEffect(() => {
		if (!orderBillTo?.isActive && !replacementOrderBillTo) {
			setValue("billTo", null);
		}
	}, [orderBillTo?.isActive, replacementOrderBillTo, setValue]);

	const handleOnSave = (values: FormSchema) => {
		dispatch(
			submittingReplacementOrderActions.updateShippingOrderDetails({
				shippingDetails: {
					shipToId: values.address?.shipToId,
					address: {
						...(watch("isCustomerPickup") ? {} : values?.address?.address),
						phoneNumber: formatPhoneNumber(values.phoneNumber),
						email: values.email
					}
				},
				labelComments: values.deliveryInstructions,
				isCustomerPickup: values.isCustomerPickup
			})
		);
		dispatch(
			submittingReplacementOrderActions.updateOrderDetails({
				poNumber: values.poNumber,
				jobName: values.jobName
			})
		);
		navigate(`/replacements/review/${params?.orderId}`);
	};

	const handleAddNewAddressModalSubmit = (address: AddressFormFields) => {
		const shippingAddress: models["ShipToViewModel"] = {
			shipToId: null,
			isActive: true,
			isSelected: true,
			address: {
				name: address.fullName,
				email: address.email,
				phoneNumber: address.contactPhoneNumber,
				line1: address.address,
				line2: address.address2,
				line3: null,
				city: address.city,
				state: address.state,
				zip: address.zip,
				county: address.county
			}
		};
		dispatch(
			submittingReplacementOrderActions.updateShippingOrderDetails({
				shippingDetails: shippingAddress,
				labelComments: null,
				isCustomerPickup: false
			})
		);
		dispatch(submittingReplacementOrderActions.addNewAddress(shippingAddress));
		setValue("address", shippingAddress);
		setCombinedAddresses((prevAddresses) => [...prevAddresses, shippingAddress]);
	};

	return (
		<form
			onSubmit={handleSubmit(handleOnSave)}
			noValidate
		>
			<ReplacementsHeader orderDetail={orderDetails} />
			<ReplacementsShippingDetailsPageContent>
				<h5>{NewOrderShippingDetailsLabel}</h5>
				<div>
					<BillToBox
						billTo={orderBillTo}
						orderType={OrderType.REPLACEMENT}
						isNoChargeOrder
						dataTestIdPrefix="replacement"
						billToSelectedIsActive={replacementOrderBillTo?.isActive}
						hasActiveBillTos={activeBillTos.length > 0}
						billToController={
							orderBillTo?.isActive || isAccountsLoading ? undefined : (
								<Controller
									name="billTo"
									control={control}
									render={({ field: { onChange, value = null } }) => (
										<AutoComplete
											options={activeBillTos ?? []}
											onChange={(_event: any, value: any) => {
												setValue("billTo", value);
												dispatch(
													submittingReplacementOrderActions.setSelectedBillToAddress(value)
												);
												onChange(value);
											}}
											getOptionLabel={(option: models["BillToViewModel"]) =>
												[option.projectName, option.billToId].filter(Boolean).join(" - ")
											}
											value={value}
											errorText=""
											label={SelectBillToText + "*"}
											dataTestId="replacements-shipping-order-details-designer"
											isLoading={isAccountsLoading}
										/>
									)}
								/>
							)
						}
					/>
				</div>
				<ShippingOrderDetails staticDesigner={orderDetails?.designer ?? UnavailableDataPlaceholderText} />
				<ShipToForm
					allowLabelComments={Boolean(accounts?.[0]?.allowLabelComments)}
					analyticsDataId="replacements-shipping-details-new-address-link"
					combinedAddresses={combinedAddresses}
					customerPickupAllowed={Boolean(accounts?.[0]?.customerPickupAllowed)}
					handleAddNewAddressModalSubmit={handleAddNewAddressModalSubmit}
					handleOnCancel={() => {}}
					handleSetShippingInformation={() => {}}
					handleOnSave={handleOnSave}
					showCancelSaveButtons={false}
					hasFormContext
					isCustomerPickupDefault={replacementOrder?.isCustomerPickup}
					selectDefaultAddress={false}
				/>
				<FlowButtons>
					<Button
						data-testid="replacement-shipping-details-back"
						variant="text"
						onClick={() => navigate(`/replacements/reason/${params?.orderId}`)}
					>
						{BackText}
					</Button>
					<Button
						data-testid="replacement-shipping-details-continue"
						type="submit"
						variant="contained"
					>
						{ContinueText}
					</Button>
				</FlowButtons>
			</ReplacementsShippingDetailsPageContent>
		</form>
	);
};

export default ReplacementsShippingDetailsPage;
